import { Seo } from '@frontend/shared/web';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFilterStore } from '../../components/filters/filterStore';
import { GlobalFilters } from '../../components/filters/GlobalFilters';
import { InvoiceDetailDrawer } from '../../components/invoices/InvoiceDetailDrawer';
import type { InvoiceDisplayStatus } from '../../components/invoices/invoiceLabels';
import { InvoicesFilters } from '../../components/invoices/InvoicesFilters';
import { InvoicesPagination } from '../../components/invoices/InvoicesPagination';
import { InvoicesTable } from '../../components/invoices/InvoicesTable';
import { Header } from '../../components/layout/Header';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { toUserErrorMessage } from '../../lib/apiError';
import { downloadFile } from '../../lib/downloadFile';
import { useExtranetInvoicesList } from '../../services/api/hooks/useExtranetInvoicesList';

const DEFAULT_LIMIT = 20;

export const InvoicesRoute = () => {
  const toast = useToast();
  const { can } = useAccount();
  const canDownloadPdf = can('invoicing');
  const { scopes, dateRange } = useFilterStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const page = Math.max(1, Number(searchParams.get('page') ?? 1));
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);
  const search = searchParams.get('search') ?? '';
  const status = (searchParams.getAll('status') as InvoiceDisplayStatus[]) ?? [];

  const query = useExtranetInvoicesList({
    scopes,
    dateRange,
    page,
    limit,
    search: search || undefined,
    status: status.length > 0 ? status : undefined,
  });

  // Le back ne livre pas toujours `totalItems` dans la pagination — on retombe
  // sur `items.length` pour éviter d'afficher "0" alors que des lignes sont rendues.
  const total = query.data?.totalItems ?? query.data?.items?.length ?? 0;

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      });
    },
    [setSearchParams],
  );

  const setSingleParam = (key: string, value: string) =>
    updateParams(p => {
      if (value) p.set(key, value);
      else p.delete(key);
      if (key !== 'page' && key !== 'limit') p.set('page', '1');
    });

  const setMultiParam = (key: string, values: string[]) =>
    updateParams(p => {
      p.delete(key);
      values.forEach(v => p.append(key, v));
      p.set('page', '1');
    });

  const handleDownloadPdf = async (item: { id: string; invoiceNumber: string }) => {
    try {
      await downloadFile(`/v1/invoices/${item.id}/pdf`, `${item.invoiceNumber}.pdf`);
    } catch (err) {
      const { title, description } = toUserErrorMessage(err, 'Téléchargement PDF échoué');
      toast.error(title, description);
    }
  };

  return (
    <>
      <Seo title="Facturation — Portail extranet" />
      <Header title="Facturation" subtitle="Consultation des factures et téléchargement des listings de courses" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-5">
          <GlobalFilters />

          <InvoicesFilters
            search={search}
            onSearchChange={v => setSingleParam('search', v)}
            status={status}
            onStatusChange={next => setMultiParam('status', next)}
            totalCount={total}
          />

          <InvoicesTable
            items={query.data?.items ?? []}
            total={total}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            isError={query.isError}
            error={query.error}
            selectedId={selectedId}
            onRowClick={item => setSelectedId(item.id)}
            onDownloadPdf={canDownloadPdf ? handleDownloadPdf : undefined}
          />

          <InvoicesPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={p => setSingleParam('page', String(p))}
            onLimitChange={l =>
              updateParams(p => {
                p.set('limit', String(l));
                p.set('page', '1');
              })
            }
          />
        </div>
      </div>

      <InvoiceDetailDrawer invoiceId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
};
